import { useEffect, useRef, useState } from 'react';
import { ToothDiagram } from './ToothDiagram';
import { toothQuadrants, toothOrderRows, findingTypes, isUpperTooth, resolveColor, multiToothCodes } from './odontogramConfig';
import { groupTeethByRow } from './odontogramUtils';

const resolveGridLabel = (finding) => {
  if (!finding?.type) return '';
  const findingDefinition = findingTypes[finding.type];
  if (!findingDefinition?.showInGrid) return '';
  return finding.subCode || findingDefinition.gridCode || '';
};

const toothColumnWidth = 47;

const EditCell = ({ className, finding, toothNum, cellKey, gridEdits, activeColor, onGridEdit, onApplyFinding }) => {
  const autoValue = resolveGridLabel(finding);
  const editKey = `${toothNum}_${cellKey}`;
  const override = gridEdits[editKey];
  const displayValue = override?.text ?? autoValue;
  const cfgColor = finding?.type ? resolveColor(finding) : null;
  const color = override ? override.color : (cfgColor || '#333');
  return (
    <input
      className={`${className} editable-grid-cell`}
      value={displayValue}
      style={{ color }}
      onChange={e => onGridEdit?.(editKey, e.target.value.toUpperCase(), activeColor)}
      onClick={() => onApplyFinding?.(toothNum)}
      maxLength={10}
    />
  );
};

const AcronymTable = ({ permanentQuadrant, toothFindings, isBottom = false, activeColor, gridEdits = {}, onGridEdit, onApplyFinding }) => {
  const getDeciduousNumber = (num) => {
    const quadrant = Math.floor(num / 10);
    const digit = num % 10;
    if (digit > 5) return null;
    const quadrantMap = { 1: 50, 2: 60, 3: 70, 4: 80 };
    return quadrantMap[quadrant] ? quadrantMap[quadrant] + digit : null;
  };

  const outerTeeth = permanentQuadrant.filter(({ num }) => !getDeciduousNumber(num));
  const innerTeeth = permanentQuadrant.filter(({ num }) => !!getDeciduousNumber(num));

  const cellClassName = 'acronym-cell';
  const borderClassName = 'acronym-cell-border-left';

  const renderEditCell = (className, extraClassName, finding, toothNumber, cellKey) => (
    <EditCell className={`${className}${extraClassName ? ` ${extraClassName}` : ''}`} finding={finding} toothNum={toothNumber} cellKey={cellKey}
      gridEdits={gridEdits} activeColor={activeColor} onGridEdit={onGridEdit}
      onApplyFinding={onApplyFinding} />
  );

  const renderOuter = (num, idx) => {
    const findings = (toothFindings[num] || []).filter(finding => !finding.surface || findingTypes[finding.type]?.showInGrid)
      .sort((a, b) => (a.surface ? 1 : 0) - (b.surface ? 1 : 0));
    const primaryFinding = findings[0] || {};
    const secondaryFinding = findings[1] || {};
    const isFirst = idx === 0;
    const columnClass = `tooth-column${isFirst ? ` ${borderClassName}` : ''}`;

    if (isBottom) {
      return (
        <div key={num} className={columnClass} style={{ width: `${toothColumnWidth}px` }}>
          <div className={`${cellClassName} empty-spacer`} />
          {renderEditCell(cellClassName, '', primaryFinding, num, 'f1')}
          {renderEditCell(`${cellClassName} last-row`, '', secondaryFinding, num, 'f2')}
        </div>
      );
    }
    return (
      <div key={num} className={columnClass} style={{ width: `${toothColumnWidth}px` }}>
        {renderEditCell(cellClassName, '', secondaryFinding, num, 'f2')}
        {renderEditCell(`${cellClassName} last-row`, '', primaryFinding, num, 'f1')}
        <div className={`${cellClassName} empty-spacer`} />
      </div>
    );
  };

  const renderInner = (num, idx) => {
    const findings = (toothFindings[num] || []).filter(finding => !finding.surface || findingTypes[finding.type]?.showInGrid)
      .sort((a, b) => (a.surface ? 1 : 0) - (b.surface ? 1 : 0));
    const primaryFinding = findings[0] || {};
    const secondaryFinding = findings[1] || {};
    const deciduousNumber = getDeciduousNumber(num);
    const deciduousFindings = deciduousNumber ? (toothFindings[deciduousNumber] || []).filter(finding => !finding.surface || findingTypes[finding.type]?.showInGrid) : [];
    const deciduousFinding = deciduousFindings[0] || {};
    const isFirst = idx === 0;
    const columnClass = `tooth-column${isFirst ? ` ${borderClassName}` : ''}`;

    if (isBottom) {
      return (
        <div key={num} className={columnClass} style={{ width: `${toothColumnWidth}px` }}>
          {renderEditCell(cellClassName, '', deciduousFinding, deciduousNumber ?? num, 'dec')}
          {renderEditCell(cellClassName, '', primaryFinding, num, 'f1')}
          {renderEditCell(`${cellClassName} last-row`, '', secondaryFinding, num, 'f2')}
        </div>
      );
    }
    return (
      <div key={num} className={columnClass} style={{ width: `${toothColumnWidth}px` }}>
        {renderEditCell(cellClassName, '', secondaryFinding, num, 'f2')}
        {renderEditCell(cellClassName, '', primaryFinding, num, 'f1')}
        {renderEditCell(`${cellClassName} last-row`, '', deciduousFinding, deciduousNumber ?? num, 'dec')}
      </div>
    );
  };

  const firstNum = permanentQuadrant[0]?.num;
  const isRightSide = (firstNum % 10) > 5;

  const outerSection = (
    <div className="acronym-section">
      {outerTeeth.map(({ num }, idx) => renderOuter(num, idx))}
    </div>
  );

  const innerSection = (
    <div className="acronym-section">
      {innerTeeth.map(({ num }, idx) => renderInner(num, idx))}
    </div>
  );

  return (
    <div className="acronym-quadrant-wrapper">
      {isRightSide ? (
        <>
          {outerSection}
          <div className="acronym-section--overlap">{innerSection}</div>
        </>
      ) : (
        <>
          {innerSection}
          <div className="acronym-section--overlap">{outerSection}</div>
        </>
      )}
    </div>
  );
};

const MultiToothOverlay = ({ chartRef, multiToothFindings = [], zoom = 1 }) => {
  const [positions, setPositions] = useState({});

  useEffect(() => {
    if (!chartRef.current) return;
    const wrapper = chartRef.current;

    const calculate = () => {
      if (!wrapper) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      if (wrapperRect.width === 0) return;
      const positionMap = {};
      wrapper.querySelectorAll('[data-tooth]').forEach(element => {
        const num = element.getAttribute('data-tooth');
        const rect = element.getBoundingClientRect();
        positionMap[num] = {
          cx: (rect.left - wrapperRect.left) / zoom + (rect.width / zoom) / 2,
          top: (rect.top - wrapperRect.top) / zoom,
          bottom: (rect.top - wrapperRect.top + rect.height) / zoom,
          width: rect.width / zoom,
        };
      });
      if (Object.keys(positionMap).length > 0) {
        setPositions(positionMap);
      }
    };

    calculate();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(calculate);
    });
    observer.observe(wrapper);

    return () => {
      observer.disconnect();
    };
  }, [multiToothFindings, chartRef, zoom]);

  if (multiToothFindings.length === 0 || Object.keys(positions).length === 0) return null;

  const wrapperWidth = (chartRef.current?.offsetWidth || 0);
  const wrapperHeight = (chartRef.current?.offsetHeight || 0);

  return (
    <svg
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
      width={wrapperWidth}
      height={wrapperHeight}
    >
      {multiToothFindings.map((multiFinding, idx) => {
        const fromPosition = positions[multiFinding.fromTooth];
        const toPosition = positions[multiFinding.toTooth];
        if (!fromPosition || !toPosition) return null;
        const color = resolveColor(multiFinding);
        const midX = (fromPosition.cx + toPosition.cx) / 2;

        if (multiFinding.code === multiToothCodes.diastema) {
          const height = Math.max(fromPosition.bottom - fromPosition.top, toPosition.bottom - toPosition.top);
          const toothNum = parseInt(multiFinding.fromTooth, 10);
          const isUpper = isUpperTooth(toothNum);
          let yStart, yEnd;
          if (isUpper) {
            yStart = Math.min(fromPosition.top, toPosition.top) + height * 0.44;
            yEnd = Math.min(fromPosition.top, toPosition.top) + height * 0.88;
          } else {
            yStart = Math.max(fromPosition.top, toPosition.top) + height * 0.12;
            yEnd = Math.max(fromPosition.top, toPosition.top) + height * 0.56;
          }
          const yMid = (yStart + yEnd) / 2;
          const curveWidth = 5;
          const leftTooth = fromPosition.cx < toPosition.cx ? fromPosition : toPosition;
          const rightTooth = fromPosition.cx < toPosition.cx ? toPosition : fromPosition;
          const leftX = leftTooth.cx + leftTooth.width * 0.42;
          const rightX = rightTooth.cx - rightTooth.width * 0.42;
          return (
            <g key={idx}>
              <path d={`M ${leftX},${yStart} Q ${leftX + curveWidth},${yMid} ${leftX},${yEnd}`}
                stroke={color} strokeWidth="2.2" fill="none" />
              <path d={`M ${rightX},${yStart} Q ${rightX - curveWidth},${yMid} ${rightX},${yEnd}`}
                stroke={color} strokeWidth="2.2" fill="none" />
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.supernumerary) {
          const toothNum = parseInt(multiFinding.fromTooth, 10);
          const isUpper = isUpperTooth(toothNum);
          const numberY = isUpper
            ? Math.min(fromPosition.top, toPosition.top) + 12
            : Math.max(fromPosition.bottom, toPosition.bottom) - 12;
          return (
            <g key={idx}>
              <circle cx={midX} cy={numberY} r="10" stroke={color} strokeWidth="1.6" fill="white" />
              <text x={midX} y={numberY + 4} textAnchor="middle" fontSize="10"
                fontWeight="bold" fill={color} fontFamily="monospace">S</text>
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.transposition) {
          const toothNum = parseInt(multiFinding.fromTooth, 10);
          const isUpper = isUpperTooth(toothNum);

          const leftPosition = fromPosition.cx < toPosition.cx ? fromPosition : toPosition;
          const rightPosition = fromPosition.cx < toPosition.cx ? toPosition : fromPosition;

          const startLeftX = leftPosition.cx - leftPosition.width * 0.25;
          const endLeftX = leftPosition.cx + leftPosition.width * 0.20;
          const startRightX = rightPosition.cx + rightPosition.width * 0.25;
          const endRightX = rightPosition.cx - rightPosition.width * 0.20;

          const arcMidX = (startLeftX + startRightX) / 2;
          const peakHeight = isUpper ? -18 : 18;
          const offset = isUpper ? -5 : 5;
          const baseY = isUpper ? Math.min(fromPosition.top, toPosition.top) + offset : Math.max(fromPosition.bottom, toPosition.bottom) + offset;

          const controlX1 = arcMidX - 10;
          const controlX2 = arcMidX + 10;
          const controlY = baseY + peakHeight;

          const angle1 = Math.atan2(baseY - controlY, endRightX - controlX1) * 180 / Math.PI;
          const angle2 = Math.atan2(baseY - controlY, endLeftX - controlX2) * 180 / Math.PI;

          return (
            <g key={idx} stroke={color} strokeWidth="1.8" fill="none">
              <path d={`M ${startLeftX},${baseY} Q ${controlX1},${controlY} ${endRightX},${baseY}`} />
              <path d={`M ${startRightX},${baseY} Q ${controlX2},${controlY} ${endLeftX},${baseY}`} />
              <g transform={`rotate(${angle1} ${endRightX} ${baseY})`}>
                <path d={`M ${endRightX - 6},${baseY - 4} L ${endRightX},${baseY} L ${endRightX - 6},${baseY + 4}`} />
              </g>
              <g transform={`rotate(${angle2} ${endLeftX} ${baseY})`}>
                <path d={`M ${endLeftX - 6},${baseY - 4} L ${endLeftX},${baseY} L ${endLeftX - 6},${baseY + 4}`} />
              </g>
            </g>
          );
        }

        const teeth = multiFinding.teeth || [parseInt(multiFinding.fromTooth), parseInt(multiFinding.toTooth)];
        const rowSegments = groupTeethByRow(teeth);
        const getSegmentPositions = (segment) => {
          const from = positions[segment.from], to = positions[segment.to];
          return (from && to) ? { from, to } : null;
        };

        if (multiFinding.code === multiToothCodes.fixedProsthesis) {
          return (
            <g key={idx} stroke={color} strokeWidth="2" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx), right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx);
                const upper = isUpperTooth(segment.from);
                const y = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) - 2 : Math.max(segmentPositions.from.bottom, segmentPositions.to.bottom) + 2;
                const bracketHeight = upper ? -8 : 8;
                return (<g key={segmentIndex}><line x1={left} y1={y} x2={left} y2={y + bracketHeight} /><line x1={left} y1={y + bracketHeight} x2={right} y2={y + bracketHeight} /><line x1={right} y1={y + bracketHeight} x2={right} y2={y} /></g>);
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.removableProsthesis) {
          return (
            <g key={idx} stroke={color} strokeWidth="1.6" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx) - 8, right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx) + 8;
                const upper = isUpperTooth(segment.from);
                const y = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) - 4 : Math.max(segmentPositions.from.bottom, segmentPositions.to.bottom) + 4;
                const gap = upper ? -5 : 5;
                return (<g key={segmentIndex}><line x1={left} y1={y} x2={right} y2={y} /><line x1={left} y1={y + gap} x2={right} y2={y + gap} /></g>);
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.totalProsthesis) {
          return (
            <g key={idx} stroke={color} strokeWidth="1.6" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx) - 16, right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx) + 16;
                const height = Math.max(segmentPositions.from.bottom - segmentPositions.from.top, segmentPositions.to.bottom - segmentPositions.to.top);
                const upper = isUpperTooth(segment.from);
                const crownY = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) + height * 0.70 : Math.max(segmentPositions.from.top, segmentPositions.to.top) + height * 0.30;
                return (<g key={segmentIndex}><line x1={left} y1={crownY - 1.5} x2={right} y2={crownY - 1.5} /><line x1={left} y1={crownY + 1.5} x2={right} y2={crownY + 1.5} /></g>);
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.totalEdentulous) {
          return (
            <g key={idx} stroke={color} strokeWidth="1.6" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx) - 16, right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx) + 16;
                const height = Math.max(segmentPositions.from.bottom - segmentPositions.from.top, segmentPositions.to.bottom - segmentPositions.to.top);
                const upper = isUpperTooth(segment.from);
                const crownY = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) + height * 0.70 : Math.max(segmentPositions.from.top, segmentPositions.to.top) + height * 0.30;
                return <line key={segmentIndex} x1={left} y1={crownY} x2={right} y2={crownY} />;
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.fixedOrthodontic) {
          return (
            <g key={idx} stroke={color} strokeWidth="1.4" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx), right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx);
                const upper = isUpperTooth(segment.from);
                const y = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) - 11 : Math.max(segmentPositions.from.bottom, segmentPositions.to.bottom) + 11;
                const squareSize = 6;
                return (
                  <g key={segmentIndex}>
                    <line x1={left + squareSize} y1={y} x2={right - squareSize} y2={y} />
                    <rect x={left - squareSize} y={y - squareSize} width={squareSize * 2} height={squareSize * 2} fill="white" />
                    <line x1={left - 3} y1={y} x2={left + 3} y2={y} /><line x1={left} y1={y - 3} x2={left} y2={y + 3} />
                    <rect x={right - squareSize} y={y - squareSize} width={squareSize * 2} height={squareSize * 2} fill="white" />
                    <line x1={right - 3} y1={y} x2={right + 3} y2={y} /><line x1={right} y1={y - 3} x2={right} y2={y + 3} />
                  </g>
                );
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.removableOrthodontic) {
          return (
            <g key={idx} stroke={color} strokeWidth="1.4" fill="none">
              {rowSegments.map((segment, segmentIndex) => {
                const segmentPositions = getSegmentPositions(segment); if (!segmentPositions) return null;
                const halfTooth = (segmentPositions.from.width || 41) / 2;
                const left = Math.min(segmentPositions.from.cx, segmentPositions.to.cx) - halfTooth - 3;
                const right = Math.max(segmentPositions.from.cx, segmentPositions.to.cx) + halfTooth + 3;
                const upper = isUpperTooth(segment.from);
                const y = upper ? Math.min(segmentPositions.from.top, segmentPositions.to.top) - 11 : Math.max(segmentPositions.from.bottom, segmentPositions.to.bottom) + 11;
                const totalWidth = right - left;
                const teethInSegment = (teeth || []).filter(toothNumber => {
                  const row = toothOrderRows.find(r => r.includes(toothNumber));
                  const segmentRow = toothOrderRows.find(r => r.includes(segment.from));
                  return row === segmentRow;
                });
                const teethCount = Math.max(1, teethInSegment.length);
                const zigCount = teethCount * 2;
                const zigWidth = totalWidth / zigCount;
                const amplitude = 4;
                let zigPath = `M ${left},${y}`;
                for (let i = 0; i < zigCount; i++) zigPath += ` L ${left + (i + 1) * zigWidth},${i % 2 === 0 ? y - amplitude : y + amplitude}`;
                return <path key={segmentIndex} d={zigPath} />;
              })}
            </g>
          );
        }

        if (multiFinding.code === multiToothCodes.fusion) {
          const isUpper = isUpperTooth(multiFinding.fromTooth);
          const height1 = fromPosition.bottom - fromPosition.top;
          const height2 = toPosition.bottom - toPosition.top;
          const y1 = isUpper ? fromPosition.top + height1 * 0.08 : fromPosition.top + height1 * 0.93;
          const y2 = isUpper ? toPosition.top + height2 * 0.08 : toPosition.top + height2 * 0.93;
          const centerY = (y1 + y2) / 2;
          const radiusX = 28;
          const radiusY = 9;
          return (
            <g key={idx} stroke={color} strokeWidth="1.8" fill="none" pointerEvents="none">
              <ellipse cx={fromPosition.cx} cy={centerY} rx={radiusX} ry={radiusY} />
              <ellipse cx={toPosition.cx} cy={centerY} rx={radiusX} ry={radiusY} />
            </g>
          );
        }

        return null;
      })}
    </svg>
  );
};

const OdontogramChart = ({
  toothFindings = {},
  multiToothFindings = [],
  onSurfaceClick,
  onApplyFinding,
  activeColor,
  gridEdits = {},
  onGridEdit,
  disabled = false,
}) => {
  const chartRef = useRef(null);
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const containerElement = outerRef.current;
    const chartElement = chartRef.current;
    if (!containerElement || !chartElement) return;

    const recalculate = () => {
      chartElement.style.transform = 'none';
      const intrinsicWidth = chartElement.scrollWidth;
      const intrinsicHeight = chartElement.scrollHeight;
      const containerWidth = containerElement.clientWidth;
      const scaleFactor = containerWidth < intrinsicWidth ? containerWidth / intrinsicWidth : 1;
      setScale(scaleFactor);
      chartElement.style.transform = scaleFactor < 1 ? `scale(${scaleFactor})` : 'none';
      containerElement.style.height = `${intrinsicHeight * scaleFactor}px`;
    };

    const resizeObserver = new ResizeObserver(recalculate);
    resizeObserver.observe(containerElement);
    recalculate();

    return () => resizeObserver.disconnect();
  }, []);

  const renderTeethRow = (rowData) => (
    <div className="odontogram-teeth-row">
      <div className="quadrant-container">
        {rowData.right.map(({ num, type }) => (
          <ToothDiagram
            key={num}
            number={num}
            type={type}
            findings={toothFindings[num] || []}
            onSurfaceClick={onSurfaceClick}
            onApplyFinding={onApplyFinding}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="quadrant-container">
        {rowData.left.map(({ num, type }) => (
          <ToothDiagram
            key={num}
            number={num}
            type={type}
            findings={toothFindings[num] || []}
            onSurfaceClick={onSurfaceClick}
            onApplyFinding={onApplyFinding}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );

  const renderTableRow = (right, left, isBottom = false) => (
    <div className="odontogram-acronym-table">
      <div className="quadrant-table">
        <AcronymTable permanentQuadrant={right} toothFindings={toothFindings} isBottom={isBottom}
          activeColor={activeColor} gridEdits={gridEdits} onGridEdit={onGridEdit}
          onApplyFinding={onApplyFinding} />
      </div>
      <div className="quadrant-table">
        <AcronymTable permanentQuadrant={left} toothFindings={toothFindings} isBottom={isBottom}
          activeColor={activeColor} gridEdits={gridEdits} onGridEdit={onGridEdit}
          onApplyFinding={onApplyFinding} />
      </div>
    </div>
  );

  return (
    <div className="odontogram-chart-outer" ref={outerRef}>
      <div
        className="odontogram-chart-wrapper"
        ref={chartRef}
      >
        <h1 className="odontogram-main-title">Odontograma</h1>

        <div id="odontogram-capture-area">
          <div className="odontogram-section-upper">
            {renderTableRow(toothQuadrants.permanentSuperior.right, toothQuadrants.permanentSuperior.left)}
          </div>
          {renderTeethRow(toothQuadrants.permanentSuperior)}

          <div className="odontogram-section-deciduous">
            {renderTeethRow(toothQuadrants.deciduousSuperior)}
            {renderTeethRow(toothQuadrants.deciduousInferior)}
          </div>

          <div className="odontogram-section-lower">
            {renderTeethRow(toothQuadrants.permanentInferior)}
            <div className="odontogram-section-lower-table">
              {renderTableRow(toothQuadrants.permanentInferior.right, toothQuadrants.permanentInferior.left, true)}
            </div>
          </div>

          <MultiToothOverlay chartRef={chartRef} multiToothFindings={multiToothFindings} zoom={scale} />
        </div>
      </div>
    </div>
  );
};

export default OdontogramChart;
